import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Feature, Point, Polygon } from 'geojson';
import type DateFilters from '../../common-components/date-filter/date-filter.model';
import type { CapellaFilters } from '../../models/capella/capella-model';
import {
  defaultBooleanFilters,
  defaultNumberFilters,
} from './capella-filters/default-filters';

dayjs.extend(utc);

interface CapellaPanelState {
  filters: CapellaFilters;
  searchArea: Feature<Point | Polygon> | null;
}

const FORMATTED_TODAY = dayjs().utc().toISOString();

const FORMATTED_ONE_YEAR_AGO = dayjs().utc().subtract(1, 'year').toISOString();

const defaultCapellaFilters: CapellaFilters = {
  startDate: FORMATTED_ONE_YEAR_AGO,
  endDate: FORMATTED_TODAY,
  collections: [
    'capella-open-data',
    'capella-slc',
    'capella-geo',
    'capella-gec',
    'capella-sicd',
    'capella-vessel-detection',
    'capella-samples',
    'capella-sidd',
    'capella-cphd',
  ],
  search: '',
  booleanFilters: defaultBooleanFilters,
  numberFilters: defaultNumberFilters,
};

export const INITIAL_CAPELLA_PANEL_STATE: CapellaPanelState = {
  filters: defaultCapellaFilters,
  searchArea: null,
};

const CapellaPanelSlice = createSlice({
  name: 'capellaPanel',
  initialState: INITIAL_CAPELLA_PANEL_STATE,
  reducers: {
    setCapellaFilters: (
      state,
      action: PayloadAction<Partial<CapellaFilters> | null>
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setSearchArea: (
      state,
      action: PayloadAction<Feature<Point | Polygon> | null>
    ) => {
      state.searchArea = action.payload;
    },
    setCapellaDates: (state, action: PayloadAction<DateFilters>) => {
      state.filters = {
        ...state.filters,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      };
    },
  },
});

export const { setCapellaFilters, setSearchArea, setCapellaDates } =
  CapellaPanelSlice.actions;

export default CapellaPanelSlice.reducer;
